import { useCallback, useEffect, useMemo, useRef } from "react";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// SOCKET
import { subscribeWebSocketMessage } from "../socketMessageState/webSocketMessageBus";
// INTERFACES
import { RootState } from "../../../app/store";
import { AckVerdict, SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
import { AckOutcome } from "../../../flatbuffers/generated/front-end/ack-outcome";
import { AckVerified } from "../../../flatbuffers/generated/front-end/ack-verified";
// UTILS
import formatUuidT from "../../../flatbuffers/display/uuid";
import { uuidToString } from "../../../utils/uuid";
import notifyActionOutcome from "../../../utils/actionOutcomeNotification";
import {
  ActionOutcome,
  resolveOutcome,
  verificationWindowMs,
} from "../../../utils/actionVerification";
import {
  ExpectedResponseIdentity,
  expectedResponseIdentities,
  observedExpectedIds,
  socketMessageIdentity,
  withheldCommandIdentity,
} from "./expectedResponseIdentity";

/**
 * Shared empty set for a command id that resolves to no identity.
 *
 * One frozen instance rather than a fresh `new Set()` per call so an unresolved
 * id cannot make a memo or an effect see a changed dependency every render.
 */
const EMPTY_KEYS: ReadonlySet<number> = new Set<number>();

/**
 * Turn a correlator verdict into an outcome.
 *
 * `Unsolicited` returns null on purpose: an answer nothing was waiting for says
 * something about the controller, but it says nothing about THIS press, and
 * reporting it against one would attribute an unrelated event to the operator's
 * last click.
 */
function outcomeFromVerdict(
  verdict: Pick<AckVerdict, "outcome" | "verified">,
): { outcome: ActionOutcome; detail: string } | null {
  if (verdict.outcome === AckOutcome.Matched) {
    return verdict.verified === AckVerified.No
      ? {
          outcome: ActionOutcome.Contradicted,
          detail:
            "The controller acknowledged this command but reported a different value, " +
            "so it did not take effect.",
        }
      : {
          // Deliberately NOT the stock Confirmed wording. That descriptor reads
          // "the controller reported the expected response", which is what
          // observing a NOMINATED message proves -- and is false here, where the
          // action may have nominated nothing at all. What actually happened is
          // that the controller answered the command itself.
          outcome: ActionOutcome.Confirmed,
          detail: "The controller acknowledged the command.",
        };
  }
  if (verdict.outcome === AckOutcome.Expired) {
    return {
      outcome: ActionOutcome.Unconfirmed,
      // Also distinct from the stock wording. This is not "a nominated message
      // failed to arrive"; it is the controller failing to answer a command on a
      // link where it answers every other one.
      detail: "The controller did not acknowledge the command.",
    };
  }
  return null;
}

/**
 * What an action nominates as proof that it worked.
 *
 * Both fields are optional and absence is the common case: an action that
 * nominates nothing reports `Sent`, never `Confirmed` and never `Unconfirmed`.
 */
export interface ActionVerification {
  /** Message ids whose arrival confirms the command was acted on. */
  expectedResponse?: readonly string[] | null;
  /** Verification window in SECONDS. 0/absent means UNSET — see the schema. */
  expectedResponseTimeout?: number | null;
  /**
   * The message id of the command this action SENDS.
   *
   * Used only to recognise the acknowledgement verdict VTS reports, which
   * repeats the commanded message rather than nominating a separate response —
   * so the verdict is identified by what was sent, not by `expectedResponse`.
   */
  commandMessageId?: string | null;
}

/**
 * Announce the result of one press. Returns the outcome, or null while the
 * answer is still pending — the window is open and the proof has not arrived.
 *
 * `commandMessageId` is the id of the command THIS press actually dispatched.
 * It matters because one hook instance serves both buttons of every two-button
 * type, and the two buttons send different commands: a TOGGLE row's OFF button
 * sends `toggleMessageId`, not `messageId`. Omit it and the press falls back to
 * the action's primary id, which is right for the one-button types and wrong
 * for every secondary.
 */
export type ReportActionOutcome = (
  label: string,
  delivered: boolean,
  commandMessageId?: string | UuidT | null,
) => ActionOutcome | null;

interface PendingVerification {
  label: string;
  expected: readonly string[];
  observed: Set<string>;
  timer: ReturnType<typeof setTimeout> | null;
  /**
   * The socket identities the command THIS press sent will come back under.
   *
   * Per-entry rather than per-hook. Both buttons of a two-button type share one
   * hook instance, so matching a verdict against a single action-wide set — and
   * then settling `pendingRef.current[0]` — announced one button's verdict under
   * the other button's label whenever both had a press outstanding.
   */
  commandKeys: ReadonlySet<number>;
  /**
   * True when this press has ALREADY been reported (as `Sent`) and is being
   * held open only in case a verdict arrives to improve on that answer.
   *
   * Such an entry must never announce on timeout: the operator has had their
   * answer, and a second toast saying the same thing in weaker words would
   * train them to ignore both.
   */
  announced: boolean;
}

/**
 * Watch the monitoring stream for the messages an action nominated, and report
 * what actually happened.
 *
 * THE BUS, NOT THE REDUCER. `subscribeWebSocketMessage` is the unsampled stream:
 * `MonitoringWebSocket` emits on it for every decoded message, BEFORE the
 * RAF/Map dedup collapses many messages into one "latest state per (bank,lift)"
 * dispatch. A confirming message that is superseded within the same animation
 * frame — which at a 5Hz broadcast and a busy car is routine — never reaches the
 * dashboard context at all, so verifying against the sampled state would report
 * Unconfirmed for commands that plainly worked.
 *
 * Scope follows the command. `usePostCommand` addresses the frame to the
 * SELECTED bank and lift, so a confirmation is only a confirmation if it comes
 * back from those. A message whose `liftId` equals its `bankId` is the group /
 * bank-wide convention and counts too — filtering it out would drop exactly the
 * confirmations that group commands produce.
 */
export default function useActionOutcome({
  expectedResponse,
  expectedResponseTimeout,
  commandMessageId,
}: ActionVerification): ReportActionOutcome {
  const { messages } = useGetAllMessages();
  const { theme, userLogged } = useSelector((state: RootState) => state.home);
  const { bankSelected, liftSelected } = useSelector((state: RootState) => state.monitoring);

  // The nominated ids, cleaned of the empty strings a saved-but-cleared field
  // leaves behind. Memoised on a SERIALISED key rather than on the array
  // identity: an action row rebuilt on every render would otherwise re-resolve
  // the whole catalog each time.
  //
  // JSON, not a joined string. A separator-joined key has to be split back
  // apart, and any id containing the separator would silently become two ids
  // that resolve to nothing — an action that could then never be confirmed.
  const cleanedIds = (expectedResponse ?? []).filter(
    (id) => typeof id === "string" && id.length > 0,
  );
  const expectedKey = JSON.stringify(cleanedIds);
  const expectedIds = useMemo(() => JSON.parse(expectedKey) as string[], [expectedKey]);
  const identities = useMemo(
    () => expectedResponseIdentities(messages, expectedIds),
    [messages, expectedIds],
  );

  // Resolve the identities a given command id will come back under. A verdict
  // repeats the COMMANDED message, so this — not `expectedResponse` — is what
  // identifies one. Resolved through the same helper as the nominated ids so
  // both sides agree on what identity means; an id that resolves to nothing
  // simply never matches, which is the same as having no verdict.
  //
  // A function of the id rather than one set for the whole action: each button
  // of a two-button type sends its own command, and a press has to be matched
  // against the one IT sent.
  const resolveCommandKeys = useCallback(
    (id: string | UuidT | null | undefined): ReadonlySet<number> => {
      const asString = typeof id === "string" ? id : formatUuidT(id as UuidT | undefined);
      if (!asString) return EMPTY_KEYS;
      const keys = new Set<number>();
      expectedResponseIdentities(messages, [asString]).forEach((identity) =>
        identity.keys.forEach((key) => keys.add(key)),
      );
      return keys;
    },
    [messages],
  );

  // Everything the socket callback reads lives behind one ref so the
  // subscription is registered ONCE. Re-subscribing on every prop change would
  // drop messages in the gap between unsubscribe and subscribe — a gap the
  // confirming frame is as likely to land in as any other.
  const latest = useRef<{
    identities: readonly ExpectedResponseIdentity[];
    resolveCommandKeys: (id: string | UuidT | null | undefined) => ReadonlySet<number>;
    defaultCommandMessageId: string | null;
    expectedIds: readonly string[];
    windowMs: number;
    theme: string;
    commandNotifications: boolean;
    bankId: string;
    liftId: string;
  }>({
    identities,
    resolveCommandKeys,
    defaultCommandMessageId: commandMessageId ?? null,
    expectedIds,
    windowMs: verificationWindowMs(expectedResponseTimeout),
    theme,
    commandNotifications: true,
    bankId: "",
    liftId: "",
  });

  latest.current = {
    identities,
    resolveCommandKeys,
    defaultCommandMessageId: commandMessageId ?? null,
    expectedIds,
    windowMs: verificationWindowMs(expectedResponseTimeout),
    theme,
    commandNotifications: Boolean(userLogged?.commandNotifications),
    bankId: formatUuidT(bankSelected?.id) || "",
    liftId: formatUuidT(liftSelected?.id) || "",
  };

  const pendingRef = useRef<PendingVerification[]>([]);

  const announce = useCallback((outcome: ActionOutcome, label: string, detail?: string) => {
    notifyActionOutcome({
      outcome,
      label,
      detail,
      theme: latest.current.theme,
      commandNotifications: latest.current.commandNotifications,
    });
  }, []);

  const inScope = useCallback((msg: SocketMessageInterface): boolean => {
    const { bankId, liftId } = latest.current;
    // Nothing selected to compare against. Accepting is the conservative
    // choice: refusing every message would make verification impossible rather
    // than merely imprecise, and the command could not have been sent at all
    // without a bank — usePostCommand refuses it.
    if (!bankId) return true;
    const msgBank = uuidToString(msg.bankId);
    if (msgBank !== bankId) return false;
    if (!liftId) return true;
    const msgLift = uuidToString(msg.liftId);
    return msgLift === liftId || msgLift === msgBank;
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeWebSocketMessage((msg) => {
      if (pendingRef.current.length === 0) return;
      if (!inScope(msg)) return;

      // A verdict answers the question directly, so it decides ahead of any
      // expectedResponse observation and regardless of whether the action
      // nominated one.
      //
      // Matched on the COMMANDED message, not on a nominated response: VTS
      // reports the verdict against the message it was answering. Deliberately
      // NOT gated on `msg.msg.active` the way observedExpectedIds is — a
      // de-energise is as much a command as an energise, and its answer is as
      // much an answer.
      const verdict = msg.ack;

      // Withheld: VTS never sent the command, and `useWithheldCommandNotices`
      // has already told the operator so — once, for every press, whichever
      // button sent it. The press it answers is settled SILENTLY here, so a
      // second toast cannot repeat the first, and the frame is never read as an
      // observation: it carries the command that did not happen, which is no
      // evidence of any expected response.
      if (verdict?.outcome === AckOutcome.Withheld) {
        const key = withheldCommandIdentity(msg);
        const target =
          key === undefined
            ? undefined
            : pendingRef.current.find((entry) => entry.commandKeys.has(key));
        if (target) {
          if (target.timer !== null) clearTimeout(target.timer);
          pendingRef.current = pendingRef.current.filter((entry) => entry !== target);
        }
        return;
      }

      if (verdict && verdict.outcome !== AckOutcome.None) {
        const key = socketMessageIdentity(msg);
        const resolved = key === undefined ? null : outcomeFromVerdict(verdict);
        if (resolved !== null) {
          // Settle the oldest press whose OWN command this verdict answers.
          // Not `pendingRef.current[0]`: both buttons of a two-button type
          // share this hook, so index 0 can easily be the other button's press
          // and its label is what the operator would have been shown.
          const index = pendingRef.current.findIndex(
            (entry) => key !== undefined && entry.commandKeys.has(key),
          );
          const target = index === -1 ? undefined : pendingRef.current[index];

          // An Expired verdict must not foreclose a still-open observation.
          // VTS's correlator window defaults to 2s while this one defaults to
          // 5s and is operator-configurable to more, so a press that nominated
          // an expectedResponse can still be confirmed by telemetry that has
          // not arrived yet. Only a press with nothing left to wait for is
          // settled by the controller's silence. A Matched verdict is strictly
          // more informative than an observation and settles either way.
          const forecloses =
            resolved.outcome === ActionOutcome.Unconfirmed && (target?.expected.length ?? 0) > 0;

          if (target && !forecloses) {
            if (target.timer !== null) clearTimeout(target.timer);
            pendingRef.current = pendingRef.current.filter((entry) => entry !== target);
            announce(resolved.outcome, target.label, resolved.detail);
            return;
          }
          if (target) return;
        }
      }

      const satisfied = observedExpectedIds(latest.current.identities, msg);
      if (satisfied.length === 0) return;

      pendingRef.current = pendingRef.current.filter((pending) => {
        satisfied.forEach((id) => pending.observed.add(id));
        const outcome = resolveOutcome({
          delivered: true,
          expectedResponse: pending.expected,
          observed: pending.observed,
          windowClosed: false,
        });
        if (outcome === null) return true;
        if (pending.timer !== null) clearTimeout(pending.timer);
        announce(outcome, pending.label);
        return false;
      });
    });

    return () => {
      unsubscribe();
      pendingRef.current.forEach((pending) => {
        if (pending.timer !== null) clearTimeout(pending.timer);
      });
      pendingRef.current = [];
    };
  }, [announce, inScope]);

  return useCallback(
    (label: string, delivered: boolean, commandMessageIdSent?: string | UuidT | null) => {
      const expected = latest.current.expectedIds;
      // The id this press actually dispatched. Falling back to the action's
      // primary keeps the one-button types working unchanged; a two-button type
      // passes its own id and no longer inherits the primary's.
      const firedId =
        commandMessageIdSent === undefined
          ? latest.current.defaultCommandMessageId
          : commandMessageIdSent;
      const firedKeys = latest.current.resolveCommandKeys(firedId);
      const immediate = resolveOutcome({
        delivered,
        expectedResponse: expected,
        windowClosed: false,
      });

      if (immediate !== null) {
        // Blocked is NOT announced here. Every path that can refuse a command
        // already called notifyCommandBlocked with the specific cause ("No bank
        // selected", "Action has no command bound", ...), which renders the same
        // Blocked descriptor. A second, generic toast would add no information
        // and would train the operator to dismiss both.
        if (immediate !== ActionOutcome.Blocked) announce(immediate, label);

        // `Sent` is not necessarily the last word. An action that nominates no
        // proof still gets one if this adapter acknowledges its commands, so
        // the press is held open for a verdict to improve on.
        //
        // Reported immediately rather than waiting, because the frontend cannot
        // know whether a verdict is coming -- only the adapter's config says so,
        // and the frontend has never seen it. Delaying every Sent on the chance
        // of an answer would make the common case worse to serve the rarer one.
        if (immediate === ActionOutcome.Sent && firedKeys.size > 0) {
          const held: PendingVerification = {
            label,
            expected,
            observed: new Set<string>(),
            timer: null,
            commandKeys: firedKeys,
            announced: true,
          };
          pendingRef.current.push(held);
          held.timer = setTimeout(() => {
            // Silent expiry: the answer was already given.
            pendingRef.current = pendingRef.current.filter((entry) => entry !== held);
          }, latest.current.windowMs);
        }
        return immediate;
      }

      const pending: PendingVerification = {
        label,
        expected,
        observed: new Set<string>(),
        timer: null,
        commandKeys: firedKeys,
        announced: false,
      };
      pendingRef.current.push(pending);
      pending.timer = setTimeout(() => {
        pendingRef.current = pendingRef.current.filter((entry) => entry !== pending);
        const settled = resolveOutcome({
          delivered: true,
          expectedResponse: pending.expected,
          observed: pending.observed,
          windowClosed: true,
        });
        // Never null once the window is closed; the fallback is here so a future
        // change to resolveOutcome cannot silently swallow the notification.
        announce(settled ?? ActionOutcome.Unconfirmed, pending.label);
      }, latest.current.windowMs);

      return null;
    },
    [announce],
  );
}
